import React, { useState, useEffect, useMemo } from 'react';
import PlayAudio from './PlayAudio';
import GameIcon from './GameIcon';

const PERSONALITY_LABELS = {
  friendly:  { label: 'Friendly',  color: 'bg-green-100 text-green-700' },
  tough:     { label: 'Tough',     color: 'bg-red-100 text-red-700' },
  haggler:   { label: 'Haggler',   color: 'bg-amber-100 text-amber-700' },
  generous:  { label: 'Generous',  color: 'bg-blue-100 text-blue-700' },
  impatient: { label: 'Impatient', color: 'bg-orange-100 text-orange-700' },
};

export default function MerchantShop({
  merchant, items, missionRequiredItems, ownedItems, cart,
  coins, budgetLimit,
  onBargain, onAddToCart, onBack, playSound, gameSettings = {}
}) {
  const [revealedPrices, setRevealedPrices] = useState({});
  const [greeting, setGreeting] = useState('');
  const [category, setCategory] = useState('all');

  // Merchant items
  const merchantItems = items?.filter(i =>
    i.merchant_id === merchant.id && i.is_active !== false
  ) || [];

  const categories = ['all', ...new Set(merchantItems.map(i => i.category).filter(Boolean).map(c => c.charAt(0).toUpperCase() + c.slice(1).toLowerCase()))];

  const filteredItems = category === 'all'
    ? merchantItems
    : merchantItems.filter(i => i.category?.toLowerCase() === category.toLowerCase());

  // Budget calculations
  const cartTotal = useMemo(() =>
    (cart || []).reduce((sum, c) => sum + (c.cartPrice || c.base_price_tokens || 0), 0),
    [cart]
  );
  const effectiveLimit = budgetLimit > 0 ? Math.min(coins, budgetLimit) : coins;
  const remaining = effectiveLimit - cartTotal;

  // Typing greeting
  useEffect(() => {
    const text = merchant.greeting_darija || merchant.greeting_english || gameSettings.shop_default_greeting || 'Marhba! Welcome to my shop!';
    let idx = 0;
    setGreeting('');
    const interval = setInterval(() => {
      idx++;
      setGreeting(text.slice(0, idx));
      if (idx >= text.length) clearInterval(interval);
    }, 30);
    return () => clearInterval(interval);
  }, [merchant]);

  const revealPrice = (itemId) => {
    playSound?.('tick');
    setRevealedPrices(prev => ({ ...prev, [itemId]: true }));
  };

  const isInCart = (itemId) => cart?.some(c => c.id === itemId);
  const isOwned = (itemId) => ownedItems?.includes(itemId);
  const isNeeded = (itemId) => {
    if (!missionRequiredItems) return false;
    return missionRequiredItems.some(ri => {
      const rid = typeof ri === 'object' ? (ri.item_id || ri.id) : ri;
      return rid === itemId;
    });
  };

  const budgetPct = effectiveLimit > 0 ? Math.min(100, (cartTotal / effectiveLimit) * 100) : 0;
  const budgetColor = budgetPct >= 90 ? 'bg-red-500' : budgetPct >= 70 ? 'bg-amber-500' : 'bg-[#2D6A4F]';

  return (
    <div className="min-h-[calc(100vh-60px)] bg-[#F5F0E8] animate-fade-up">
      <div className="max-w-5xl mx-auto px-4 py-4 game-content">
        {/* Back button */}
        <button
          onClick={onBack}
          className="flex items-center gap-1.5 text-gray-500 hover:text-gray-800 transition-colors text-sm mb-4"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          {gameSettings.shop_back_button || 'Back to Map'}
        </button>

        {/* Merchant Profile */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 mb-4">
          <div className="flex items-start gap-4">
            {/* Avatar */}
            <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-[#F5F0E8] flex items-center justify-center text-3xl sm:text-4xl flex-shrink-0 merchant-avatar-ring overflow-hidden">
              {merchant.image_url && (merchant.image_url.startsWith('http') || merchant.image_url.startsWith('/')) ? (
                <img src={merchant.image_url} alt="" className="w-full h-full object-cover" loading="lazy" />
              ) : (
                <span><GameIcon icon={merchant.image_url && !merchant.image_url.startsWith('/') ? merchant.image_url : (gameSettings.icon_default_merchant || '🧔')} /></span>
              )}
            </div>

            <div className="flex-1 min-w-0">
              <h2 className="font-bold text-gray-900 text-lg sm:text-xl">
                {merchant.name || merchant.name_english || 'Merchant'}
              </h2>
              <div className="flex items-center gap-1.5 flex-wrap mt-0.5">
                {merchant.specialty && (
                  <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-[#C84B31]/10 text-[#C84B31] border border-[#C84B31]/20">
                    {merchant.specialty}
                  </span>
                )}
                {merchant.personality && PERSONALITY_LABELS[merchant.personality] && (
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${PERSONALITY_LABELS[merchant.personality].color}`}>
                    {PERSONALITY_LABELS[merchant.personality].label}
                  </span>
                )}
                <span className="text-[10px] text-gray-400">{merchantItems.length} items</span>
              </div>

              {/* Greeting bubble */}
              <div className="mt-2 bg-[#F5F0E8] rounded-xl px-3 py-2 relative">
                <p className="text-sm text-gray-700 italic">
                  "{greeting}"
                  {greeting.length < (merchant.greeting_darija || merchant.greeting_english || gameSettings.shop_default_greeting || 'Marhba! Welcome to my shop!').length && (
                    <span className="typing-cursor" />
                  )}
                </p>
                {merchant.greeting_english && merchant.greeting_darija && (
                  <p className="text-[11px] text-gray-400 mt-0.5">{merchant.greeting_english}</p>
                )}
                <div className="absolute -left-2 top-3 w-0 h-0 border-t-[6px] border-t-transparent border-r-[8px] border-r-[#F5F0E8] border-b-[6px] border-b-transparent" />
              </div>
            </div>
          </div>
        </div>

        {/* Budget Bar */}
        <div className="bg-white rounded-xl border border-gray-100 p-3 mb-4">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs font-semibold text-gray-700">{gameSettings.shop_budget_label || 'Budget'}</span>
            <div className="flex items-center gap-3 text-xs">
              <span className="text-gray-500">
                Cart: <span className="font-bold text-gray-800"><GameIcon icon={gameSettings.icon_token || '🪙'} /> {cartTotal}</span>
              </span>
              <span className={`font-bold ${remaining < 0 ? 'text-red-600' : remaining < 50 ? 'text-amber-600' : 'text-[#2D6A4F]'}`}>
                Left: <GameIcon icon={gameSettings.icon_token || '🪙'} /> {remaining}
              </span>
              {budgetLimit > 0 && (
                <span className="text-gray-400">/ {budgetLimit} limit</span>
              )}
            </div>
          </div>
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ${budgetColor}`}
              style={{ width: `${Math.min(100, budgetPct)}%` }}
            />
          </div>
          {remaining < 0 && (
            <p className="text-[10px] text-red-600 font-semibold mt-1">{gameSettings.shop_over_budget || 'Over budget! Remove items from cart or bargain for lower prices.'}</p>
          )}
        </div>

        {/* Category Filter */}
        {categories.length > 2 && (
          <div className="flex gap-2 mb-4 overflow-x-auto pb-1 scrollbar-hide">
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setCategory(cat)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-colors ${
                  category === cat
                    ? 'bg-[#2D6A4F] text-white'
                    : 'bg-white text-gray-600 border border-gray-200 hover:border-[#2D6A4F]'
                }`}
              >
                {cat === 'all' ? (gameSettings.shop_all_items || 'All Items') : cat}
              </button>
            ))}
          </div>
        )}

        {/* Items Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 animate-stagger">
          {filteredItems.map(item => {
            const priceRevealed = revealedPrices[item.id];
            const inCart = isInCart(item.id);
            const owned = isOwned(item.id);
            const needed = isNeeded(item.id);
            const itemPrice = item.base_price_tokens || item.price || 0;
            const tooExpensive = priceRevealed && !owned && !inCart && itemPrice > remaining;

            return (
              <div
                key={item.id}
                className={`bg-white rounded-xl border overflow-hidden transition-shadow ${
                  needed ? 'border-[#2D6A4F] shadow-sm' : 'border-gray-100'
                } ${owned ? 'opacity-60' : 'hover:shadow-md'}`}
              >
                {/* Item Image */}
                <div className="py-3 sm:py-4 relative bg-gradient-to-br from-[#F5F0E8] to-[#E8DFD0] flex items-center justify-center">
                  {item.image_url && (item.image_url.startsWith('http') || item.image_url.startsWith('/')) ? (
                    <img src={item.image_url} alt={item.name_english || ''} className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl object-cover" loading="lazy" />
                  ) : (
                    <span className="text-3xl sm:text-4xl">
                      <GameIcon icon={item.image_url && !item.image_url.startsWith('/') ? item.image_url : (item.icon || gameSettings.icon_default_item || '👕')} />
                    </span>
                  )}

                  {/* Badges */}
                  {needed && !owned && (
                    <span className="absolute top-1 right-1 bg-[#2D6A4F] text-white text-[8px] font-bold px-1.5 py-0.5 rounded-full">
                      {gameSettings.shop_needed_badge || 'NEEDED'}
                    </span>
                  )}
                  {item.is_rare && !owned && (
                    <span className={`absolute ${needed ? 'top-5' : 'top-1'} right-1 bg-purple-600 text-white text-[8px] font-bold px-1.5 py-0.5 rounded-full`}>
                      {gameSettings.shop_rare_badge || 'RARE'}
                    </span>
                  )}
                  {item.stock_quantity > 0 && item.stock_quantity <= 3 && (
                    <span className="absolute top-1 left-1 bg-red-500 text-white text-[8px] font-bold px-1.5 py-0.5 rounded-full animate-last-one">
                      Last {item.stock_quantity}!
                    </span>
                  )}
                  {item.is_traditional && (
                    <span className="absolute bottom-1 left-1 bg-amber-600 text-white text-[8px] font-bold px-1.5 py-0.5 rounded-full">
                      {gameSettings.shop_traditional_badge || 'Traditional'}
                    </span>
                  )}
                  {owned && (
                    <span className="absolute top-1 right-1 bg-gray-600 text-white text-[8px] font-bold px-1.5 py-0.5 rounded-full">
                      {gameSettings.shop_owned_badge || 'OWNED'}
                    </span>
                  )}
                </div>

                {/* Item Info */}
                <div className="p-2.5">
                  <h4 className="font-semibold text-gray-900 text-xs truncate">
                    {item.name_english || item.name || 'Item'}
                  </h4>
                  {item.name_darija && (
                    <div className="flex items-center gap-1 justify-center">
                      <p className="text-[10px] text-[#2D6A4F] font-medium truncate">{item.name_darija}</p>
                      <PlayAudio url={item.audio_url} size="xs" />
                    </div>
                  )}
                  {item.description && (
                    <p className="text-[9px] text-gray-400 mt-0.5 line-clamp-2">{item.description}</p>
                  )}

                  {/* Price or "Ask" button */}
                  {!priceRevealed && !owned && !inCart ? (
                    <button
                      onClick={() => revealPrice(item.id)}
                      className="mt-2 w-full py-1.5 rounded-lg bg-[#F5F0E8] hover:bg-[#E8DFD0] text-[#2D6A4F] text-xs font-semibold transition-colors"
                    >
                      {gameSettings.shop_price_button || 'Bsh7al? 💬'}
                    </button>
                  ) : (
                    <div className="mt-2">
                      {(priceRevealed || owned || inCart) && (
                        <div className="flex items-center gap-1.5 mb-1.5">
                          <p className={`text-sm font-bold ${tooExpensive ? 'text-red-600' : 'text-amber-700'}`}>
                            <GameIcon icon={gameSettings.icon_token || '🪙'} /> {itemPrice}
                          </p>
                          {tooExpensive && (
                            <span className="text-[8px] font-bold text-red-500 bg-red-50 px-1.5 py-0.5 rounded">
                              {gameSettings.shop_over_budget_badge || 'OVER BUDGET'}
                            </span>
                          )}
                        </div>
                      )}

                      {owned ? (
                        <div className="text-center text-[10px] text-gray-400 py-1">{gameSettings.shop_already_owned || 'Already owned'}</div>
                      ) : inCart ? (
                        <div className="text-center text-[10px] text-green-600 py-1 font-semibold">{gameSettings.shop_in_cart || 'In cart ✓'}</div>
                      ) : priceRevealed ? (
                        <div className="grid grid-cols-2 gap-1.5">
                          <button
                            onClick={() => { playSound?.('bargain'); onBargain(item, merchant); }}
                            className={`py-1.5 rounded-lg text-white text-[10px] font-bold transition-colors ${
                              tooExpensive
                                ? 'bg-[#C84B31] hover:bg-[#E8725A] ring-1 ring-[#C84B31]/30'
                                : 'bg-[#C84B31] hover:bg-[#E8725A]'
                            }`}
                          >
                            {gameSettings.shop_bargain_button || (tooExpensive ? 'Bargain!' : 'Bargain')}
                          </button>
                          <button
                            onClick={() => { if (!tooExpensive) { playSound?.('coin'); onAddToCart(item); } }}
                            disabled={tooExpensive}
                            className={`py-1.5 rounded-lg text-[10px] font-bold transition-colors ${
                              tooExpensive
                                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                : 'bg-[#2D6A4F] hover:bg-[#40916C] text-white'
                            }`}
                          >
                            {gameSettings.shop_buy_button || 'Buy'}
                          </button>
                        </div>
                      ) : null}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {filteredItems.length === 0 && (
          <div className="text-center py-12 text-gray-400">
            <span className="text-4xl block mb-2"><GameIcon icon={gameSettings.icon_empty_state || '🏪'} /></span>
            <p className="text-sm">{gameSettings.shop_empty_category || 'No items in this category'}</p>
          </div>
        )}
      </div>
    </div>
  );
}
